import React, { useState, useMemo, useEffect } from "react";
import classNames from "classnames";
import ReactDatetime from "components/CustomDatePicker";
import moment from "moment";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { useUpdateInventoryExpense, useDeleteInventoryExpense, useUpdateInventory } from "../../../api/inventory/service";
import { formatPriceWithDollar } from "../../../utils/currency";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { getSelectStyles } from "../../../utils/selectStyles";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { formatDateForAPI, formatDateForPicker, formatDateForDisplay } from "../../../utils/dateUtils";
import { useTranslation } from 'react-i18next';
import Select from "components/Select";
import { useGetChannelsInfinite } from "api/channels/service";

const SERVICE_COMPANY_TYPES = ["repair_service_shop", "dealer_wholesaler_auction"];
const SERVICE_COMPANY_PAGE_SIZE = 50;

const ExpensesTab = ({ permission, inventoryId, inventoryData, activeColor = "secondary", isTheme2 = false }) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(false);
  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const customSelectStyles = useMemo(() => getSelectStyles(activeColorStyle, isLightTheme), [activeColorStyle, isLightTheme]);
  const selectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : customSelectStyles,
    [isTheme2, activeColorStyle, customSelectStyles]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".expenses-tab.expenses-tab--theme2",
            fieldsSelector: ".expenses-tab-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2
    ? { fontSize: "14px" }
    : undefined;
  const getDateInputProps = (extra = {}) => {
    const { className, ...rest } = extra;
    return {
      className: classNames("form-control", className),
      style: isTheme2 ? { fontSize: "14px" } : undefined,
      ...rest,
    };
  };
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [form, setForm] = useState({
    service_date: new Date(),
    class_code: '',
    service_company: '',
    new_service_company: '',
    amount: '',
    invoice_ref: '',
    payment_method: '',
    date_paid: '',
    description: '',
  });

  const [errors, setErrors] = useState({});
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const updateExpenseMutation = useUpdateInventoryExpense();
  const updateMutation = useUpdateInventory();
  const deleteExpenseMutation = useDeleteInventoryExpense();

  const [serviceCompanySearch, setServiceCompanySearch] = useState("");
  const [debouncedServiceCompanySearch, setDebouncedServiceCompanySearch] = useState("");
  const [selectedServiceCompanyOption, setSelectedServiceCompanyOption] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedServiceCompanySearch(serviceCompanySearch.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [serviceCompanySearch]);

  const {
    results: serviceCompanies,
    isLoading: isServiceCompaniesLoading,
    isFetchingNextPage: isFetchingMoreServiceCompanies,
    hasNextPage: hasMoreServiceCompanies,
    fetchNextPage: fetchMoreServiceCompanies,
  } = useGetChannelsInfinite(
    SERVICE_COMPANY_TYPES,
    SERVICE_COMPANY_PAGE_SIZE,
    debouncedServiceCompanySearch
  );

  const getCompanyDisplayName = (company) =>
    company?.legal_name || company?.name || "";

  const serviceCompanyOptions = useMemo(
    () =>
      serviceCompanies.map((company) => ({
        value: String(company.id),
        label: getCompanyDisplayName(company) || String(company.id),
      })),
    [serviceCompanies]
  );

  // Expenses data from parent component
  const expenses = inventoryData?.expenses || [];
  const expensesTotal = useMemo(() => {
    // is_active=false rows keep their amount but are excluded from Total Costs
    return expenses.reduce((sum, expense) => {
      if (expense?.is_active === false) return sum;
      const n = parseFloat(expense?.amount);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [expenses]);

  const resolveServiceCompanyLabel = (id) => {
    if (!id) return "";
    if (
      selectedServiceCompanyOption &&
      String(selectedServiceCompanyOption.value) === String(id) &&
      selectedServiceCompanyOption.label
    ) {
      return selectedServiceCompanyOption.label;
    }
    const fromOptions = serviceCompanyOptions.find((o) => o.value === String(id));
    if (fromOptions?.label) return fromOptions.label;
    const fromLoaded = serviceCompanies.find((c) => String(c.id) === String(id));
    const loadedName = getCompanyDisplayName(fromLoaded);
    if (loadedName) return loadedName;
    const fromExpense = expenses.find(
      (e) => String(e.service_company?.id || e.service_company) === String(id)
    );
    const expenseName = getCompanyDisplayName(fromExpense?.service_company);
    if (expenseName) return expenseName;
    return String(id);
  };

  const getServiceCompanySelectValue = (id) => {
    if (!id) return null;
    return {
      value: String(id),
      label: resolveServiceCompanyLabel(id),
    };
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    const newErrors = { ...errors };
    if (!value && ['service_company', 'invoice_ref', 'description'].includes(field)) {
      newErrors[field] = 'Required';
    } else {
      delete newErrors[field];
    }
    setErrors(newErrors);
  };

  const handleDateChange = (field, date) => {
    setForm(prev => ({ ...prev, [field]: date }));
    const newErrors = { ...errors };
    if (!date && ['service_date'].includes(field)) {
      newErrors[field] = 'Required';
    } else {
      delete newErrors[field];
    }
    setErrors(newErrors);
  };

  const handleExpenseAmountChange = (value) => {
    setForm(prev => ({ ...prev, amount: value }));
    const newErrors = { ...errors };
    if (!value) {
      newErrors.amount = 'Required';
    } else {
      delete newErrors.amount;
    }
    setErrors(newErrors);
  };

  const handleOpenAddForm = () => {
    setShowAddForm(true);
    setShowEditForm(false);
    setSelectedServiceCompanyOption(null);
    setServiceCompanySearch("");
    setForm({
      service_date: new Date(),
      class_code: '',
      service_company: '',
      new_service_company: '',
      amount: '',
      invoice_ref: '',
      payment_method: '',
      date_paid: '',
      description: '',
    });
    setEditForm({});
    setErrors({
      amount: t('expensesTab.expenseAmountRequired'),
      service_company: 'Required',
      invoice_ref: 'Required',
      description: 'Required',
    });
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setSelectedServiceCompanyOption(null);
    setServiceCompanySearch("");
    setForm({
      service_date: new Date(),
      class_code: '',
      service_company: '',
      new_service_company: '',
      amount: '',
      invoice_ref: '',
      payment_method: '',
      date_paid: '',
      description: '',
    });
    setEditForm({
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.service_date) newErrors.service_date = t('expensesTab.serviceDateRequired');
    if (!form.amount) newErrors.amount = t('expensesTab.expenseAmountRequired');
    if (!form.service_company) newErrors.service_company = 'Required';
    if (!form.invoice_ref) newErrors.invoice_ref = 'Required';
    if (!form.description) newErrors.description = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePostExpense = () => {
    if (!validateForm()) {
      return;
    }

    const expenseData = {
      service_date: form.service_date ? moment(form.service_date).format('YYYY-MM-DD') : null,
      class_code: form.class_code,
      service_company: form.service_company ? { id: form.service_company } : null,
      new_service_company: form.new_service_company,
      amount: form.amount ? Number(String(form.amount).replace(/[^\d.\-]/g, '')) : 0,
      invoice_ref: form.invoice_ref,
      payment_method: form.payment_method,
      date_paid: form.date_paid ? moment(form.date_paid).format('YYYY-MM-DD') : null,
      description: form.description,
      is_edit: showEditForm,
    };

    updateMutation.mutate({
      id: inventoryId,
      data: {
        expenses: expenseData
      }
    }, {
      onSuccess: () => {
        handleCloseAddForm();
      },
      onError: (error) => {
        console.error("Error adding expense:", error);
      }
    });
  };

  const formatDate = (dateStr) => {
    return formatDateForDisplay(dateStr);
  };

  const formatUSD = (value) => {
    if (value === null || value === undefined || value === "") return "$0.00";
    const num = parseFloat(value);
    if (!Number.isFinite(num)) return "$0.00";
    if (num < 0) {
      const abs = Math.abs(num).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `($${abs})`;
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  };

  const handleEditClick = (expense) => {
    setShowEditForm(true);
    setShowAddForm(false);
    setServiceCompanySearch("");
    const companyId = expense.service_company?.id
      ? String(expense.service_company.id)
      : expense.service_company
        ? String(expense.service_company)
        : "";
    const companyLabel = getCompanyDisplayName(expense.service_company);
    setSelectedServiceCompanyOption(
      companyId
        ? { value: companyId, label: companyLabel || companyId }
        : null
    );
    setEditForm({
      ...expense,
      service_company: companyId,
      service_date: formatDateForPicker(expense.service_date),
      date_paid: formatDateForPicker(expense.date_paid),
      amount: expense.amount ? formatPriceWithDollar(expense.amount) : '',
      is_active: expense.is_active,
    });

    // Set immediate edit errors for empty required fields
    const initialEditErrors = {};
    if (!expense.service_date) initialEditErrors.service_date = 'Required';
    if (!expense.amount) initialEditErrors.amount = 'Required';
    if (!expense.service_company && !expense.service_company?.id) initialEditErrors.service_company = 'Required';
    if (!expense.invoice_ref) initialEditErrors.invoice_ref = 'Required';
    if (!expense.description) initialEditErrors.description = 'Required';
    setEditErrors(initialEditErrors);
  };


  const handleEditInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    const newErrors = { ...editErrors };
    if (!value && ['service_company', 'invoice_ref', 'description', 'amount'].includes(field)) {
      newErrors[field] = 'Required';
    } else {
      delete newErrors[field];
    }
    setEditErrors(newErrors);
  };

  const handleServiceCompanyChange = (option) => {
    setSelectedServiceCompanyOption(
      option ? { value: String(option.value), label: option.label } : null
    );
    if (showEditForm) {
      handleEditInputChange("service_company", option?.value || "");
    } else {
      handleFormChange("service_company", option?.value || "");
    }
  };

  const handleEditDateChange = (field, date) => {
    setEditForm(prev => ({ ...prev, [field]: date }));
    const newErrors = { ...editErrors };
    if (!date && ['service_date'].includes(field)) {
      newErrors[field] = 'Required';
    } else {
      delete newErrors[field];
    }
    setEditErrors(newErrors);
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (!editForm.service_date) newErrors.service_date = t('expensesTab.serviceDateRequired');
    if (!editForm.amount) newErrors.amount = t('expensesTab.expenseAmountRequired');
    if (!editForm.service_company) newErrors.service_company = 'Required';
    if (!editForm.invoice_ref) newErrors.invoice_ref = 'Required';
    if (!editForm.description) newErrors.description = 'Required';
    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!validateEditForm()) {
      return;
    }

    const updatedExpense = {
      ...editForm,
      service_company: editForm.service_company ? { id: editForm.service_company } : null,
      service_date: editForm.service_date ? moment(editForm.service_date).format('YYYY-MM-DD') : null,
      date_paid: editForm.date_paid ? moment(editForm.date_paid).format('YYYY-MM-DD') : null,
      amount: editForm.amount ? Number(String(editForm.amount).replace(/[^\d.\-]/g, '')) : 0,
    };

    updateMutation.mutate({
      id: inventoryId,
      data: {
        expenses: updatedExpense
      }
    }, {
      onSuccess: () => {
        setShowEditForm(false);
        setEditForm({});
        setEditErrors({});
      },
      onError: (error) => {
        console.error("Error updating expense:", error);
      }
    });
  };

  const handleDelete = (id, onSuccessCallback) => {
    deleteExpenseMutation.mutate(
      { id },
      {
        onSuccess: () => {
          setSelectedExpense(null);
          if (typeof onSuccessCallback === 'function') {
            onSuccessCallback();
          }
        },
        onError: (error) => {
          console.error("Error deleting expense:", error);
        },
      }
    );
  };

  const openDeleteModal = (id) => {
    setExpenseToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setExpenseToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!expenseToDelete) return;
    handleDelete(expenseToDelete, () => {
      setDeleteModalOpen(false);
      setExpenseToDelete(null);
    });
  };

  const paymentMethodOptions = [
    { value: 'ach', label: t('expensesTab.ach') },
    { value: 'ap', label: t('expensesTab.ap') },
    { value: 'arm', label: t('expensesTab.arm') },
    { value: 'cash', label: t('expensesTab.cash') },
    { value: 'check', label: t('expensesTab.check') },
    { value: 'credit_card', label: t('expensesTab.creditCard') },
    { value: 'draft', label: t('expensesTab.draft') },
    { value: 'floor_plan', label: t('expensesTab.floorPlan') },
    { value: 'repossession', label: t('expensesTab.repossession') },
    { value: 'trade_in', label: t('expensesTab.tradeIn') },
  ];

  return (
    <div className={classNames("expenses-tab", { "expenses-tab--theme2": isTheme2 })}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      {!showAddForm && !showEditForm ? (
        <>
          <div className="d-flex justify-content-end mb-3">
            {permission?.inventory_expenses_add && (
              <Button
                onClick={handleOpenAddForm}
                style={{ ...activeColorStyle, color: 'white' }}
              >
                <i className="tim-icons icon-simple-add mr-2" />
                {t('expensesTab.addExpense')}
              </Button>
            )}
          </div>

          <Card>
            <CardBody>
              {expenses.length === 0 ? (
                <div className="text-center py-4">
                  <i className="tim-icons icon-money-coins" style={{ fontSize: '3rem', color: '#ccc' }} />
                  <p className="mt-3 text-muted">{t('expensesTab.noExpensesFound')}</p>
                </div>
              ) : (
                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>{t('expensesTab.serviceDate')}</th>
                      <th>{t('expensesTab.serviceCompany')}</th>
                      <th>{t('expensesTab.description')}</th>
                      <th>{t('expensesTab.invoiceRef')}</th>
                      <th>{t('expensesTab.expenseAmount')}</th>
                      <th ></th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((expense, index) => (
                      <tr
                        key={expense.documentId || index}
                        onClick={() => handleEditClick(expense)}
                        style={{ cursor: 'pointer' }}
                        className={selectedExpense?.documentId === expense.documentId ? 'table-active' : ''}
                      >
                        <td>{formatDate(expense.service_date)}</td>
                        <td>{typeof expense.service_company === 'object' && expense.service_company !== null ? expense.service_company.name : expense.service_company || '-'}</td>
                        <td>{expense.description || '-'}</td>
                        <td>{expense.invoice_ref || '-'}</td>
                        <td>{formatUSD(expense.amount)}</td>
                        <td style={{ display: "flex", justifyContent: "flex-end" }}>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(expense);
                            }}
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          {permission?.inventory_expenses_delete && expense.is_active && (
                            <Button
                              size="sm"

                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteModal(expense.documentId);
                              }}
                            >
                              <i className="tim-icons icon-simple-remove" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {expenses.length > 0 && (
                <div className="d-flex justify-content-end mt-3 pt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                  <strong style={{ fontSize: "1rem" }}>
                    {t("expensesTab.totalCosts", { defaultValue: "Total Costs" })}:{" "}
                    {formatUSD(expensesTotal)}
                  </strong>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle tag="h4">
              {showEditForm ? t('expensesTab.editExpense') : t('expensesTab.addNewExpense')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <Form
              className={classNames({ "expenses-tab-fields": isTheme2 })}
              onSubmit={showEditForm ? handleEditSubmit : (e) => { e.preventDefault(); handlePostExpense(); }}
            >
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="service_date">{t('expensesTab.serviceDate')} *</Label>
                    <ReactDatetime
                      inputProps={getDateInputProps({
                        placeholder: t('expensesTab.selectServiceDate'),
                        className: (showEditForm ? editErrors : errors).service_date ? "is-invalid" : "",
                      })}
                      value={showEditForm ? editForm.service_date : form.service_date}
                      onChange={(date) => showEditForm ? handleEditDateChange('service_date', date) : handleDateChange('service_date', date)}
                      dateFormat="MM-DD-YYYY"
                      timeFormat={false}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="amount">{t('expensesTab.expenseAmount')} *</Label>
                    <Input
                      type="text"
                      value={showEditForm ? editForm.amount : form.amount}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d.]/g, '');
                        showEditForm
                          ? handleEditInputChange('amount', raw)
                          : handleExpenseAmountChange(raw);
                      }}
                      onBlur={() => {
                        showEditForm
                          ? handleEditInputChange('amount', formatPriceWithDollar(editForm.amount))
                          : handleExpenseAmountChange(formatPriceWithDollar(form.amount));
                      }}
                      onFocus={() => {
                        showEditForm
                          ? handleEditInputChange('amount', String(editForm.amount || '').replace(/[^\d.]/g, ''))
                          : handleExpenseAmountChange(String(form.amount || '').replace(/[^\d.]/g, ''));
                      }}
                      className={classNames(
                        (showEditForm ? editErrors : errors).amount ? "is-invalid" : "",
                        { "form-control": isTheme2 }
                      )}
                      style={inputStyle}
                      placeholder={t('expensesTab.enterAmount')}
                      disabled={updateMutation.isPending || updateExpenseMutation.isPending || (showEditForm && !editForm.is_active)}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="service_company">{t('expensesTab.serviceCompany')} *</Label>
                    <div
                      className={(showEditForm ? editErrors : errors).service_company ? 'is-invalid' : ''}
                    >
                      <Select
                        styles={selectStyles}
                        className="react-select"
                        placeholder={t('expensesTab.enterServiceCompany')}
                        name="service_company"
                        isSearchable={true}
                        closeMenuOnSelect={true}
                        filterOption={null}
                        isLoading={isServiceCompaniesLoading || isFetchingMoreServiceCompanies}
                        value={
                          showEditForm
                            ? getServiceCompanySelectValue(editForm.service_company)
                            : getServiceCompanySelectValue(form.service_company)
                        }
                        onChange={handleServiceCompanyChange}
                        onInputChange={(newValue, { action }) => {
                          if (action === 'input-change') {
                            setServiceCompanySearch(newValue);
                          }
                          if (action === 'menu-close') {
                            setServiceCompanySearch('');
                          }
                          return newValue;
                        }}
                        onMenuScrollToBottom={() => {
                          if (hasMoreServiceCompanies && !isFetchingMoreServiceCompanies) {
                            fetchMoreServiceCompanies();
                          }
                        }}
                        options={serviceCompanyOptions}
                        isDisabled={updateMutation.isPending || updateExpenseMutation.isPending || (showEditForm && !editForm.is_active)}
                        isClearable
                      />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="invoice_ref">Invoice/Ref Number *</Label>
                    <Input
                      type="text"
                      name="invoice_ref"
                      value={showEditForm ? editForm.invoice_ref : form.invoice_ref}
                      onChange={(e) => showEditForm ? handleEditInputChange('invoice_ref', e.target.value) : handleFormChange('invoice_ref', e.target.value)}
                      placeholder="Enter Invoice/Ref"
                      disabled={updateMutation.isPending || updateExpenseMutation.isPending || (showEditForm && !editForm.is_active)}
                      className={classNames(
                        (showEditForm ? editErrors : errors).invoice_ref ? "is-invalid" : "",
                        { "form-control": isTheme2 }
                      )}
                      style={inputStyle}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="payment_method">{t('expensesTab.paymentMethod')}</Label>
                    <Select
                      styles={selectStyles}
                      className="react-select"
                      placeholder="Select payment method"
                      name="payment_method"
                      isSearchable={false}
                      closeMenuOnSelect={true}
                      value={showEditForm ? editForm.payment_method ? {
                        value: editForm.payment_method,
                        label: paymentMethodOptions.find(option => option.value === editForm.payment_method)?.label || editForm.payment_method
                      } : null : form.payment_method ? {
                        value: form.payment_method,
                        label: paymentMethodOptions.find(option => option.value === form.payment_method)?.label || form.payment_method
                      } : null}
                      onChange={(option) => showEditForm ? handleEditInputChange('payment_method', option?.value || '') : handleFormChange('payment_method', option?.value || '')}
                      options={paymentMethodOptions}
                      isDisabled={updateMutation.isPending || updateExpenseMutation.isPending || (showEditForm && !editForm.is_active)}
                      isClearable
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="date_paid">{t('expensesTab.datePaid')}</Label>
                    <ReactDatetime
                      inputProps={getDateInputProps({
                        placeholder: t('expensesTab.selectDatePaid'),
                        disabled: updateMutation.isPending || updateExpenseMutation.isPending || (showEditForm && !editForm.is_active),
                      })}
                      value={showEditForm ? editForm.date_paid : form.date_paid}
                      onChange={(date) => showEditForm ? handleEditDateChange('date_paid', date) : handleDateChange('date_paid', date)}
                      dateFormat="MM-DD-YYYY"
                      timeFormat={false}
                      disabled={updateMutation.isPending || updateExpenseMutation.isPending || (showEditForm && !editForm.is_active)}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label for="description">{t('expensesTab.description')} *</Label>
                    <Input
                      type="textarea"
                      value={showEditForm ? editForm.description : form.description}
                      onChange={(e) => showEditForm ? handleEditInputChange('description', e.target.value) : handleFormChange('description', e.target.value)}
                      placeholder={t('expensesTab.enterDescription')}
                      disabled={updateMutation.isPending || updateExpenseMutation.isPending || (showEditForm && !editForm.is_active)}
                      className={classNames(
                        (showEditForm ? editErrors : errors).description ? "is-invalid" : "",
                        { "form-control": isTheme2 }
                      )}
                      style={inputStyle}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      color="secondary"
                      onClick={handleCloseAddForm}
                      disabled={updateMutation.isPending || updateExpenseMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      style={{ ...activeColorStyle, color: 'white' }}
                      disabled={updateMutation.isPending || updateExpenseMutation.isPending || (showEditForm && !editForm.is_active) || Object.keys(showEditForm ? editErrors : errors).length > 0}
                    >
                      {(updateMutation.isPending || updateExpenseMutation.isPending) ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {showEditForm ? 'Updating...' : 'Adding...'}
                        </>
                      ) : (
                        <>
                          <i className={showEditForm ? "tim-icons icon-pencil" : "tim-icons icon-simple-add"} style={{ marginRight: '8px' }} />
                          {showEditForm ? t('expensesTab.updateExpense') : t('expensesTab.addExpense')}
                        </>
                      )}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      )}

      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('expensesTab.deleteExpense')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>Are you sure you want to delete this expense?</p>
          <p className='text-muted'>{t('expensesTab.thisActionCannotBeUndone')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteExpenseMutation.isPending || deleteExpenseMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white'
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteExpenseMutation.isPending || deleteExpenseMutation.isLoading}
          >
            {deleteExpenseMutation.isPending || deleteExpenseMutation.isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              'Yes, Delete'
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ExpensesTab; 